import { clsx } from 'clsx'

import { sizeClassMap, validationClassMap, type SearchableSelectValidationState, type SelectOption } from './types'

interface TriggerButtonProps<T> {
  buttonRef: React.RefObject<HTMLButtonElement>
  id?: string
  isOpen: boolean
  disabled?: boolean
  size: 'sm' | 'md' | 'lg'
  validationState: SearchableSelectValidationState
  className?: string
  selectedOption: SelectOption<T> | null
  placeholder: string
  clearable: boolean
  renderValue?: (option: SelectOption<T>) => React.ReactNode
  onClick: () => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onClear: (e: React.MouseEvent) => void
}

interface ButtonContentProps<T> {
  selectedOption: SelectOption<T> | null
  renderValue?: (option: SelectOption<T>) => React.ReactNode
  placeholder: string
}

function TriggerButtonContent<T>({ selectedOption, renderValue, placeholder }: ButtonContentProps<T>): JSX.Element {
  if (selectedOption && renderValue) { return <span className="truncate min-w-0 block">{renderValue(selectedOption)}</span> }
  if (selectedOption) { return <span className="truncate min-w-0 block">{selectedOption.label}</span> }
  return <span className="truncate min-w-0 block text-muted-foreground">{placeholder}</span>
}

export function TriggerButton<T>(p: TriggerButtonProps<T>): JSX.Element {
  return (
    <div className="relative">
      <button
        ref={p.buttonRef}
        aria-controls={p.isOpen ? `${p.id ?? 'searchable-select'}-listbox` : undefined}
        aria-expanded={p.isOpen}
        aria-haspopup="listbox"
        aria-invalid={p.validationState === 'invalid'}
        className={clsx(
          'el-input el-select-button',
          sizeClassMap[p.size],
          validationClassMap[p.validationState],
          'pr-10',
          p.className,
        )}
        disabled={p.disabled}
        id={p.id}
        role="combobox"
        type="button"
        onClick={p.onClick}
        onKeyDown={p.onKeyDown}
      >
        <TriggerButtonContent placeholder={p.placeholder} renderValue={p.renderValue} selectedOption={p.selectedOption} />
      </button>
      <TriggerIcons clearable={p.clearable} disabled={p.disabled} selectedOption={p.selectedOption} onClear={p.onClear} />
    </div>
  )
}

interface TriggerIconsProps<T> {
  clearable: boolean
  selectedOption: SelectOption<T> | null
  disabled?: boolean
  onClear: (e: React.MouseEvent) => void
}

function TriggerIcons<T>({ clearable, selectedOption, disabled, onClear }: TriggerIconsProps<T>): JSX.Element {
  return (
    <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
      {clearable && selectedOption && !disabled && (
        <button
          aria-label="Clear selection"
          className="p-1 text-muted-foreground hover:text-foreground rounded"
          tabIndex={-1}
          type="button"
          onClick={onClear}
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
        </button>
      )}
      <div className="text-muted-foreground pointer-events-none">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      </div>
    </div>
  )
}
